import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, EditIcon, BookOpenIcon, LockIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { COAFilters } from '../../components/coa/COAFilters';
import { COATreeView } from '../../components/coa/COATreeView';
import { ChartOfAccount, COAFilters as Filters } from '../../types/coa.types';
import { coaService } from '../../services/coa.service';
export function COAListPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [treeAccounts, setTreeAccounts] = useState<ChartOfAccount[]>([]);
  const [filters, setFilters] = useState<Filters>({});
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadAccounts();
  }, [filters]);
  const loadAccounts = async () => {
    setLoading(true);
    try {
      const data = await coaService.getAccounts(filters);
      setAccounts(data);
      const tree = await coaService.getAccountTree();
      setTreeAccounts(tree);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleResetFilters = () => {
    setFilters({});
  };
  const handleAccountClick = (account: ChartOfAccount) => {
    navigate(`/coa/${account.id}`);
  };
  return <div className="min-h-screen bg-gray-50 p-6" data-id="element-1091">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-1092">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1093">
          <div data-id="element-1094">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1095">
              Chart of Accounts
            </h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1096">
              Manage your account hierarchy and structure
            </p>
          </div>
          <Button onClick={() => navigate('/coa/new')} data-id="element-1097">
            <PlusIcon className="w-4 h-4 mr-2" data-id="element-1098" />
            Create Account
          </Button>
        </div>

        {/* Filters */}
        <COAFilters filters={filters} onFilterChange={setFilters} onReset={handleResetFilters} data-id="element-1099" />

        {/* View Toggle */}
        <div className="flex gap-2" data-id="element-1100">
          <Button variant={viewMode === 'table' ? 'primary' : 'secondary'} size="sm" onClick={() => setViewMode('table')} data-id="element-1101">
            Table View
          </Button>
          <Button variant={viewMode === 'tree' ? 'primary' : 'secondary'} size="sm" onClick={() => setViewMode('tree')} data-id="element-1102">
            Tree View
          </Button>
        </div>

        {/* Content */}
        {loading ? <div className="bg-white rounded-lg border p-12 text-center text-gray-500" data-id="element-1103">
            Loading accounts...
          </div> : viewMode === 'tree' ? <COATreeView accounts={treeAccounts} onAccountClick={handleAccountClick} data-id="element-1104" /> : <div className="bg-white rounded-lg border overflow-hidden" data-id="element-1105">
            <div className="overflow-x-auto" data-id="element-1106">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-1107">
                <thead className="bg-gray-50" data-id="element-1108">
                  <tr data-id="element-1109">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1110">
                      Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1111">
                      Account Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1112">
                      Parent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1113">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1114">
                      Subtype
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1115">
                      Branch
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1116">
                      Bank
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1117">
                      Balance
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase" data-id="element-1118">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-1119">
                  {accounts.length === 0 ? <tr data-id="element-1120">
                      <td colSpan={9} className="px-6 py-12 text-center text-gray-500" data-id="element-1121">
                        No accounts found. Create your first account to get
                        started.
                      </td>
                    </tr> : accounts.map(account => <tr key={account.id} className="hover:bg-gray-50" data-id="element-1122">
                        <td className="px-6 py-4 text-sm font-mono text-gray-900" data-id="element-1123">
                          {account.accountCode}
                        </td>
                        <td className="px-6 py-4" data-id="element-1124">
                          <button onClick={() => handleAccountClick(account)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1125">
                            {account.accountName}
                          </button>
                          {account.isInactive && <Badge variant="warning" className="ml-2" data-id="element-1126">
                              Inactive
                            </Badge>}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1127">
                          {account.parentAccount?.accountName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1128">
                          {account.accountType}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1129">
                          {account.subType}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1130">
                          {account.branchName || '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600" data-id="element-1131">
                          {account.bankName ? <div data-id="element-1132">
                              <div data-id="element-1133">{account.bankName}</div>
                              <div className="text-xs text-gray-500" data-id="element-1134">
                                {account.bankAccountNo}
                              </div>
                            </div> : '-'}
                        </td>
                        <td className="px-6 py-4" data-id="element-1135">
                          <button onClick={() => navigate(`/coa/${account.id}/ledger`)} className="text-sm font-medium text-blue-600 hover:text-blue-800" data-id="element-1136">
                            ${account.balance?.toLocaleString() || '0'}
                          </button>
                        </td>
                        <td className="px-6 py-4" data-id="element-1137">
                          <div className="flex items-center gap-2" data-id="element-1138">
                            <button onClick={() => navigate(`/coa/${account.id}/ledger`)} className="text-gray-600 hover:text-gray-900" title="View Ledger" data-id="element-1139">
                              <BookOpenIcon className="w-4 h-4" data-id="element-1140" />
                            </button>
                            <button onClick={() => navigate(`/coa/${account.id}/edit`)} className="text-gray-600 hover:text-gray-900" title={account.hasTransactions ? 'Limited edit (has transactions)' : 'Edit'} disabled={account.hasTransactions} data-id="element-1141">
                              {account.hasTransactions ? <LockIcon className="w-4 h-4 text-gray-400" data-id="element-1142" /> : <EditIcon className="w-4 h-4" data-id="element-1143" />}
                            </button>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>}
      </div>
    </div>;
}